import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, MenuItem, TextField,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import { MODALITIES, guessModality, inspectOmeTiff, uploadImage } from '../../services';
import { FIREBASE_ENV } from '../Firebase/config';

// L'émulateur Storage garde chaque fichier en mémoire d'un seul bloc : au-delà de ~2 Go, il plante.
const EMULATOR_MAX_BYTES = 1e9;

const formatSize = bytes => (bytes >= 1e9 ? `${(bytes / 1e9).toFixed(1)} GB` : `${Math.round(bytes / 1e6)} MB`);
const plural = (n, word) => `${n} ${word}${n > 1 ? 's' : ''}`;

// Fichier de la liste : checking -> valid | invalid, puis queued -> uploading -> done | error | canceled.
let lastKey = 0;
const newItem = file => ({
  key: ++lastKey, file, status: 'checking', info: null, error: null, guessed: null, modality: '', label: '',
  progress: 0, state: null,
});
const EDITABLE = ['checking', 'valid', 'invalid', 'error', 'canceled'];

// Ajout d'images (modalités) à un échantillon, une ou plusieurs à la fois : en-tête de chaque fichier vérifié dans le
// navigateur, modalité et nom proposés d'après le nom du fichier, puis envoi des fichiers l'un après l'autre (le
// serveur découpe le premier pendant l'envoi des suivants), chacun reprenable, avec sa progression.
// existing = images déjà présentes (pour comparer les dimensions et repérer les modalités en double).
export default function AddImageDialog({ open, onClose, profile, projectId, sampleId, existing }) {
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const inspections = useRef(Promise.resolve());
  const current = useRef(null);   // { key, task } : envoi en cours
  const canceled = useRef(false);

  useEffect(() => {
    if (open && !running) setItems([]);
  }, [open]);

  const patch = (key, changes) => setItems(list => list.map(it => (it.key === key ? { ...it, ...changes } : it)));

  // En-têtes lus l'un après l'autre (un fichier à la fois en mémoire).
  const inspect = async item => {
    if (FIREBASE_ENV === 'emulator' && item.file.size > EMULATOR_MAX_BYTES) {
      patch(item.key, {
        status: 'invalid',
        error: 'File too large for the local emulator (1 GB at most): test real slides on preprod (npm run dev).',
      });
      return;
    }
    try {
      const info = await inspectOmeTiff(item.file);
      const guessed = guessModality(item.file.name);
      patch(item.key, { status: 'valid', info, guessed, modality: guessed?.key || '', label: guessed?.label || '' });
    } catch (err) {
      patch(item.key, { status: 'invalid', error: err.message });
    }
  };

  const addFiles = fileList => {
    const known = itemsRef.current.map(it => `${it.file.name}|${it.file.size}`);
    const added = [...(fileList || [])].filter(f => !known.includes(`${f.name}|${f.size}`)).map(newItem);
    if (!added.length) return;
    setItems(list => [...list, ...added]);
    added.forEach(item => { inspections.current = inspections.current.then(() => inspect(item)); });
  };
  const remove = key => setItems(list => list.filter(it => it.key !== key));

  // Images déjà présentes (affichables ou en préparation), hors envois interrompus ou en cours.
  const present = existing.filter(img => ['ready', 'processing'].includes(img.status) || img.devUrl);
  const batch = items.filter(it => it.info && it.status !== 'invalid');
  // Référence pour les dimensions : une image déjà présente, sinon le premier fichier lisible de la liste.
  const refFor = item => {
    const img = present.find(i => i.width && i.height);
    if (img) return { name: img.label || img.modality, width: img.width, height: img.height };
    const other = batch.find(it => it.key !== item.key);
    return other && other.key < item.key ? { name: other.file.name, ...other.info } : null;
  };
  const duplicateOf = item => item.modality && (present.some(img => img.modality === item.modality)
    || batch.some(it => it.key !== item.key && it.modality === item.modality));

  // À envoyer : fichiers lisibles pas encore envoyés, y compris après un échec ou une annulation (nouvel essai).
  const valid = items.filter(it => it.info && ['valid', 'error', 'canceled'].includes(it.status));
  const complete = it => it.modality && it.label.trim();
  const checking = items.some(it => it.status === 'checking');
  const canSend = !running && !checking && valid.length > 0 && valid.every(complete);

  const sendAll = async () => {
    canceled.current = false;
    setRunning(true);
    const queue = valid.map(it => it.key);
    queue.forEach(key => patch(key, { status: 'queued', error: null }));
    let failures = 0;
    for (const key of queue) {
      const item = itemsRef.current.find(it => it.key === key);
      if (!item) continue;  // retiré de la liste pendant l'attente
      if (canceled.current) { patch(key, { status: 'canceled', error: 'Upload canceled.' }); failures += 1; continue; }
      patch(key, { status: 'uploading', progress: 0, state: 'running' });
      const job = uploadImage(profile, projectId, sampleId, item.file,
        { modality: item.modality, label: item.label.trim(), info: item.info },
        (progress, state) => {
          if (canceled.current) job.task()?.cancel();  // annulation demandée avant que l'envoi ait commencé
          patch(key, { progress, state });
        });
      current.current = { key, task: job.task };
      try {
        await job.done;
        patch(key, { status: 'done', progress: 1 });
      } catch (err) {
        failures += 1;
        const cancel = err.code === 'storage/canceled';
        patch(key, { status: cancel ? 'canceled' : 'error', error: cancel ? 'Upload canceled.' : `Upload failed: ${err.message}` });
      }
    }
    current.current = null;
    setRunning(false);
    if (!failures) onClose();  // tout est parti : la barre latérale suit la préparation de chaque image
  };
  const cancelAll = () => {
    canceled.current = true;
    current.current?.task()?.cancel();
  };

  const uploading = items.find(it => it.status === 'uploading');
  const sent = items.filter(it => it.status === 'done').length;
  const sending = items.filter(it => ['queued', 'uploading', 'done'].includes(it.status)).length;
  const options = item => (item.guessed && !MODALITIES.some(m => m.key === item.guessed.key)
    ? [...MODALITIES, item.guessed] : MODALITIES);

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Add images</DialogTitle>
      <DialogContent>
        <p style={{ fontSize: 13, color: 'rgb(110,110,110)', marginTop: 0 }}>
          One or more pyramidal OME-TIFF files from the acquisition platform. Only the header of each file is read for
          the check; the files are then uploaded one after the other, in chunks (resumed automatically if the connection
          drops). Keep this window open during the upload.
        </p>
        {/* Glisser-déposer depuis le Finder : évite la fenêtre « Ouvrir » de macOS, qui génère des aperçus et
            décode les WSI entiers en mémoire (processus ImageThumbnailExtension). */}
        <div
          className={`add-image-drop${dragOver ? ' over' : ''}${running ? ' disabled' : ''}`}
          onDragOver={e => { e.preventDefault(); if (!running) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            if (!running) addFiles(e.dataTransfer.files);
          }}
        >
          Drag one or more OME-TIFF files here from the Finder
          <span>recommended for large slides: the macOS "Open" window computes very heavy previews</span>
        </div>
        <Button variant="outlined" component="label" disabled={running}>
          {items.length ? 'Add more files…' : 'or choose files…'}
          <input type="file" hidden multiple accept=".tif,.tiff" data-testid="image-file"
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        </Button>

        {items.map(item => {
          const ref = item.info && refFor(item);
          const mismatch = ref && (ref.width !== item.info.width || ref.height !== item.info.height);
          const editable = !running && EDITABLE.includes(item.status);
          return (
            <div key={item.key} className={`add-image-item ${item.status}`}>
              <div className="add-image-item-head">
                <span className="add-image-item-name">{item.file.name}</span>
                <span className="add-image-item-size">{formatSize(item.file.size)}</span>
                {(editable || item.status === 'queued') && (
                  <IconButton size="small" aria-label={`Remove ${item.file.name}`} onClick={() => remove(item.key)}>
                    <Close fontSize="inherit" />
                  </IconButton>
                )}
              </div>
              {item.status === 'checking' && <LinearProgress sx={{ my: 1 }} />}
              {item.info && (
                <p className="add-image-item-info">
                  {item.info.width.toLocaleString('en-US')} × {item.info.height.toLocaleString('en-US')} px · {item.info.levels} levels ·
                  {' '}{item.info.rgb ? 'color (RGB)' : `${item.info.channels} ${item.info.channels > 1 ? 'channels' : 'channel'}`} · {item.info.dtype}
                </p>
              )}
              {item.info && (
                <div className="add-image-item-fields">
                  <TextField select size="small" label="Modality" value={item.modality} disabled={!editable}
                    sx={{ flex: 1 }}
                    onChange={e => patch(item.key, {
                      modality: e.target.value,
                      label: options(item).find(m => m.key === e.target.value)?.label || item.label,
                    })}>
                    {options(item).map(m => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
                  </TextField>
                  <TextField size="small" label="Display name" value={item.label} disabled={!editable} sx={{ flex: 1 }}
                    onChange={e => patch(item.key, { label: e.target.value })} />
                </div>
              )}
              {valid.includes(item) && !complete(item) && (
                <Alert severity="warning" sx={{ mt: 1 }}>Choose a modality and a name.</Alert>
              )}
              {mismatch && EDITABLE.includes(item.status) && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Dimensions differ from "{ref.name}" ({ref.width.toLocaleString('en-US')} ×{' '}
                  {ref.height.toLocaleString('en-US')}): it won't be overlaid on the others. First rewrite the scans on a
                  common pixel grid (tools/registration/auto).
                </Alert>
              )}
              {duplicateOf(item) && EDITABLE.includes(item.status) && (
                <Alert severity="info" sx={{ mt: 1 }}>Another image of this sample already has this modality.</Alert>
              )}
              {['uploading', 'done'].includes(item.status) && (
                <div style={{ marginTop: 8 }}>
                  <LinearProgress variant="determinate" value={Math.round(item.progress * 100)} />
                  <p className="add-image-item-progress">
                    {item.status === 'done' ? 'Uploaded · the server is preparing the display'
                      : `${item.state === 'paused' ? 'Paused' : 'Uploading'} · ${Math.round(item.progress * 100)}%`}
                  </p>
                </div>
              )}
              {item.status === 'queued' && <p className="add-image-item-progress">Waiting</p>}
              {item.error && <Alert severity="error" sx={{ mt: 1 }}>{item.error}</Alert>}
            </div>
          );
        })}
      </DialogContent>
      <DialogActions>
        {running && (
          <span style={{ marginRight: 'auto', marginLeft: 16, fontSize: 13 }}>
            {sent} / {plural(sending, 'image')} uploaded
          </span>
        )}
        {running ? (
          <>
            {uploading?.state === 'paused'
              ? <Button onClick={() => current.current?.task()?.resume()}>Resume</Button>
              : <Button onClick={() => current.current?.task()?.pause()} disabled={!uploading}>Pause</Button>}
            <Button color="error" onClick={cancelAll}>Cancel uploads</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="contained" disableElevation onClick={sendAll} disabled={!canSend}>
              {valid.length > 1 ? `Upload ${valid.length} images` : 'Upload'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
